import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFormType } from '@neo/blocks/forms/types'
import { ButtonWithLoader } from '@neo/index'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { Tooltip } from '@neo/primitives/tooltip'
import { useMutationState } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { useRef, useState } from 'react'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { useGetKnowledgeBases } from '../queries'
import type { NewKBFormValues } from '../types'
import { documentSetupFields } from './document-setup.config'

// Non-blocking duplicate-name warning: rag enforces NO name uniqueness, so we warn (not block) when
// the typed name collides with an existing KB — gives instant feedback before any uploads/crawl.
const DuplicateNameWarning = ({ form }: { form: GenericFormType<NewKBFormValues> }) => {
  const name = form.useStore((s) => s.values.name)
  const { data: kbs } = useGetKnowledgeBases()
  const trimmed = (name ?? '').trim().toLowerCase()
  const isDup = !!trimmed && (kbs ?? []).some((kb) => kb.name.trim().toLowerCase() === trimmed)
  if (!isDup) return null
  return (
    <div
      className="flex items-start gap-1 text-status-warning"
      data-testid="warning-duplicate-kb-name"
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-status-warning" />
      <span className="text-xsi-400">
        A knowledge base named &quot;{name}&quot; already exists. You can still create it, but the
        name will be duplicated.
      </span>
    </div>
  )
}

type SetupProps = {
  form: GenericFormType<NewKBFormValues>
}

export const Setup = ({ form }: SetupProps) => {
  // Retrieval-mode (Default/Docs) dropdown is gated behind its own kbDocsModeEnabled flag, so docs
  // mode can be turned on for a single org (e.g. plivo) independently of the rag rollout.
  const { kbDocsModeEnabled } = useFeatureFlags()
  const mutationState = useMutationState({
    filters: {
      mutationKey: flowQueryKeys().createKnowledgeBase,
    },
    select: (mutation) => mutation.state.status === 'pending',
  }).some((m) => m)

  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const scrollAreaRef = useRef<HTMLDivElement>(null)

  const handleNext = async () => {
    const errors = await form.validateAllFields('change')
    setValidationErrors(errors.filter((error): error is string => typeof error === 'string'))
    if (errors.length > 0) {
      // Find the first error field and scroll to it
      const firstErrorField = document.querySelector('.field-error-message')
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      return
    }
    setValidationErrors([])
    form.setFieldValue('step', 2)
    form.validateField('step', 'change')
  }

  return (
    <div className="flex flex-col h-full">
      <ScrollArea ref={scrollAreaRef} className="flex-1 pb-6">
        <div className="mt-6 w-[60%] grid gap-4 ml-0.5 pb-4">
          <FieldsRenderer<NewKBFormValues>
            form={form}
            fields={documentSetupFields(kbDocsModeEnabled)}
          />
          <DuplicateNameWarning form={form} />
        </div>
      </ScrollArea>
      <div className="sticky bottom-3 bg-background pt-1">
        <div className="w-[60%] flex items-center justify-end gap-2">
          {validationErrors.length > 0 && (
            <Tooltip content={validationErrors.join('\n')}>
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </Tooltip>
          )}
          <ButtonWithLoader
            type="button"
            data-testid="button-finish"
            loading={mutationState}
            onClick={handleNext}
          >
            Next
          </ButtonWithLoader>
        </div>
      </div>
    </div>
  )
}
